import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Crosshair, Loader2, MapPin, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocationAutocomplete } from "@/api/hooks";
import type { SearchLocationMode } from "@/api/types";
import { LocationAttribution } from "@/components/forms/location-attribution";
import { DEBOUNCE_MS } from "@/features/search/types";
import { useDebounce } from "@/hooks/use-debounce";
import { useGeolocation } from "@/hooks/use-geolocation";
import { cn } from "@/lib/cn";
import { formatLocationLabel } from "@/lib/format-location-label";
import {
  KEYBOARD_SCROLL_CONTAINER_ATTR,
  MOBILE_VISUAL_VIEWPORT_SHELL_CLASS,
} from "@/lib/overlay-keyboard-layout";
import { resolveLocationLabel } from "@/lib/resolve-location-label";
import { getRecentLocations, type RecentLocation, saveRecentLocation } from "./recent-locations";

interface SelectedLocation {
  label: string;
  lat: number | null;
  lng: number | null;
  bounds: string | null;
  locationMode: SearchLocationMode | null;
}

interface MobileLocationOverlayProps {
  onClose: () => void;
  onSelect: (location: SelectedLocation) => void;
  showReset: boolean;
  onReset: () => void;
}

// fallow-ignore-next-line complexity
function MobileLocationOverlay({
  onClose,
  onSelect,
  showReset,
  onReset,
}: MobileLocationOverlayProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const previousFocusRef = useRef(document.activeElement);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, DEBOUNCE_MS);
  const showAutocomplete = debouncedQuery.trim().length >= 2;
  const { data, isLoading, isError, isFetching, refetch } = useLocationAutocomplete(
    debouncedQuery.trim(),
  );
  const searchBusy = isLoading || query !== debouncedQuery;
  const geo = useGeolocation();
  const cancelGeolocation = geo.cancel;
  const nearMeRequest = useRef<AbortController | null>(null);
  const [resolvingNearMe, setResolvingNearMe] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const recentLocations = useMemo(() => getRecentLocations(), []);

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 100);
    return () => {
      clearTimeout(timer);
      nearMeRequest.current?.abort();
    };
  }, []);

  function cancelNearMe() {
    cancelGeolocation();
    nearMeRequest.current?.abort();
    nearMeRequest.current = null;
    setResolvingNearMe(false);
    setLookupError(null);
  }

  function handleClose() {
    cancelNearMe();
    onClose();
  }

  function handleSelect(location: SelectedLocation) {
    cancelNearMe();
    saveRecentLocation(location);
    onSelect(location);
  }

  const nearMeDenied = geo.permissionDenied;
  const nearMeBusy = geo.loading || resolvingNearMe;
  const nearMeLabel = nearMeBusy ? "Locating..." : nearMeDenied ? geo.error : "Near me";

  function handleNearMe() {
    cancelNearMe();
    const request = new AbortController();
    nearMeRequest.current = request;
    geo.request((lat, lng) => {
      if (request.signal.aborted) return;
      void (async () => {
        setResolvingNearMe(true);
        try {
          const label = await resolveLocationLabel(lat, lng, request.signal);
          if (request.signal.aborted) return;
          handleSelect({ label, lat, lng, bounds: null, locationMode: "coords" });
        } catch (error) {
          if (
            request.signal.aborted ||
            (error instanceof DOMException && error.name === "AbortError")
          )
            return;
          setLookupError(
            "Unable to look up your location. Try Near me again or search for an area.",
          );
        } finally {
          if (!request.signal.aborted) {
            nearMeRequest.current = null;
            setResolvingNearMe(false);
          }
        }
      })();
    });
  }

  function handleAutocompleteSelect(location: {
    name: string;
    region: string | null;
    lat: number;
    lng: number;
    bounds: string | null;
  }) {
    const label = formatLocationLabel(location.name, location.region);
    handleSelect({
      label,
      lat: location.lat,
      lng: location.lng,
      bounds: location.bounds,
      locationMode: "place",
    });
  }

  const content = (
    <motion.div
      className={cn(
        MOBILE_VISUAL_VIEWPORT_SHELL_CLASS,
        "z-[70] flex flex-col bg-paper pt-[env(safe-area-inset-top)]",
      )}
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-fog px-3 py-2">
        <button
          type="button"
          className="inline-flex size-10 shrink-0 items-center justify-center rounded-full text-ink transition-colors hover:bg-fog"
          onClick={handleClose}
          aria-label="Back"
        >
          <ArrowLeft className="size-5" strokeWidth={2} />
        </button>
        <DialogPrimitive.Title asChild>
          <span className="min-w-0 flex-1 text-[15px] font-semibold text-ink">Choose location</span>
        </DialogPrimitive.Title>
        {showReset && (
          <button
            type="button"
            className="shrink-0 px-2 py-1 text-[14px] font-medium text-slate transition-colors active:text-electric-800"
            onClick={() => {
              cancelNearMe();
              onReset();
            }}
            aria-label="Reset location"
          >
            Reset
          </button>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2 border-b border-fog px-4 py-3">
        <Search className="size-4 shrink-0 text-slate" />
        <input
          ref={inputRef}
          type="text"
          aria-label="Search location"
          className="min-w-0 flex-1 bg-transparent text-[16px] text-ink outline-none placeholder:text-slate"
          placeholder="Search location..."
          value={query}
          onChange={(event) => {
            cancelNearMe();
            setQuery(event.target.value);
          }}
        />
      </div>

      <div
        {...{ [KEYBOARD_SCROLL_CONTAINER_ATTR]: "" }}
        className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-4 pb-[env(safe-area-inset-bottom)]"
      >
        <button
          type="button"
          className={cn(
            "mt-3 flex w-full items-center gap-3 rounded-sm border bg-surface px-3 py-3 text-left transition-colors",
            nearMeDenied
              ? "border-danger/30 hover:border-danger/50"
              : "border-fog hover:border-electric-700",
          )}
          onClick={handleNearMe}
          disabled={nearMeBusy}
        >
          {nearMeBusy ? (
            <Loader2 className="size-4 shrink-0 animate-spin text-electric-800" />
          ) : (
            <Crosshair
              className={cn("size-4 shrink-0", nearMeDenied ? "text-danger" : "text-electric-800")}
            />
          )}
          <span
            className={cn(
              "min-w-0 flex-1 truncate font-medium",
              nearMeDenied ? "text-[13px] text-danger" : "text-[14px] text-electric-800",
            )}
          >
            {nearMeLabel}
          </span>
        </button>

        {((geo.error && !nearMeDenied) || lookupError) && (
          <p role="alert" className="pt-2 text-[14px] text-danger">
            {geo.error || lookupError}
          </p>
        )}

        {showAutocomplete && searchBusy && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="size-5 animate-spin text-slate" />
          </div>
        )}

        {showAutocomplete && !searchBusy && isError && (
          <div role="alert" className="py-4 text-[14px] text-danger">
            Unable to load places.{" "}
            <button
              type="button"
              className="font-semibold text-electric-800 underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-electric-700"
              disabled={isFetching}
              onClick={() => void refetch()}
            >
              Try again
            </button>
          </div>
        )}

        {showAutocomplete && !searchBusy && !isError && (data?.suggestions?.length ?? 0) === 0 && (
          <p className="py-8 text-center text-[14px] text-slate">
            No location results for &ldquo;{debouncedQuery.trim()}&rdquo;
          </p>
        )}

        {showAutocomplete && !searchBusy && !isError && (data?.suggestions?.length ?? 0) > 0 && (
          <div className="pt-3">
            <span className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.06em] text-slate">
              Autocomplete
            </span>
            <div className="flex flex-col gap-1">
              {data?.suggestions?.map((suggestion) => (
                <button
                  key={`${suggestion.lat}-${suggestion.lng}-${suggestion.name}`}
                  type="button"
                  className="flex items-center gap-3 rounded-sm px-2 py-2.5 text-left transition-colors hover:bg-fog"
                  onClick={() => handleAutocompleteSelect(suggestion)}
                >
                  <MapPin className="size-4 shrink-0 text-slate" />
                  <span className="text-[14px] text-ink">
                    {suggestion.name}
                    {suggestion.region && <span className="text-slate">, {suggestion.region}</span>}
                  </span>
                </button>
              ))}
            </div>
            <LocationAttribution className="px-2" />
          </div>
        )}

        {!showAutocomplete && (
          <div className="pt-3 pb-8">
            <span className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.06em] text-slate">
              Previous locations
            </span>
            {recentLocations.length > 0 ? (
              <div className="flex flex-col gap-1">
                {recentLocations.map((location) => (
                  <button
                    key={getRecentLocationKey(location)}
                    type="button"
                    className="flex items-center gap-3 rounded-sm px-2 py-2.5 text-left transition-colors hover:bg-fog"
                    onClick={() => handleSelect(location)}
                  >
                    <Clock className="size-4 shrink-0 text-slate" />
                    <span className="text-[14px] text-ink">{location.label}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="py-4 text-[14px] text-slate">
                Select a location once and it will appear here.
              </p>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );

  return (
    <DialogPrimitive.Root open onOpenChange={(open) => !open && handleClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          asChild
          aria-modal="true"
          aria-describedby={undefined}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (previousFocusRef.current instanceof HTMLElement) previousFocusRef.current.focus();
          }}
        >
          {content}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function getRecentLocationKey(location: RecentLocation): string {
  return `${location.label}|${location.lat ?? ""}|${location.lng ?? ""}|${location.bounds ?? ""}|${location.locationMode ?? ""}`;
}

export { MobileLocationOverlay, type SelectedLocation };
